import { PaymentMethods } from '@/enums';
import { BidderAddress } from '@/types/BidderAddresses';

import { ApiHosts, handleResponseWithNon200Errors, makePost } from './helpers';

/**
 * @category Mainhost API
 * @see postLAPayment
 */
type PostLAPaymentParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
    installmentFee: number;
    invoiceId: number;
    paymentMethod: PaymentMethods;
    paymentToken?: string;
    phoneNumber: string;
    riskifiedSessionId?: string;
    shippingAddress: BidderAddress;
    shippingMethod?: string;
    signifydSessionId?: string;
};
/**
 * postLAPayment - posts payment data to mainhost's LA Payments / Payrix API receiver
 * @category Mainhost API
 * @function postLAPayment
 * @param {PostLAPaymentParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/api/bidders/${bidderId}/checkout/${invoiceId}
 * @see https://github.com/LIVEauctioneers/mainhost-prod/blob/master/httpdocs/api/bidders.php
 */
export const postLAPayment = ({
    authToken,
    bidderId,
    deployment,
    installmentFee,
    invoiceId,
    paymentMethod,
    paymentToken,
    phoneNumber,
    riskifiedSessionId,
    shippingAddress,
    shippingMethod,
    signifydSessionId,
}: PostLAPaymentParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: `api/bidders/${bidderId}/checkout/${invoiceId}`,
            authToken,
            deployment,
            path: ApiHosts.Mainhost,
        });
        request.send({
            installmentFee,
            paymentToken,
            paymentType: paymentMethod,
            riskifiedSessionId,
            shippingAddress: {
                address1: shippingAddress.address1,
                address2: shippingAddress.address2,
                address3: shippingAddress.address3,
                addressId: shippingAddress.addressId,
                city: shippingAddress.city,
                countryCode: shippingAddress.countryCode,
                phoneNumber,
                postalCode: shippingAddress.postalCode,
                shippingOptionType: shippingMethod,
                state: shippingAddress.state,
            },
            signifydSessionId,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

/**
 * @category Payments API
 * @see getProcessingFees
 */
type GetProcessingFeesParams = {
    authToken: string;
    balanceDue: number;
    catalogId: number;
    deployment: string;
};
/**
 * getProcessingFees - returns processing fees for a catalog
 * @category Payments API
 * @function getProcessingFees
 * @param {GetProcessingFees}
 * @see https://redstripe-DEPLOYMENT.liveauctioneers.com/payment-api/catalog/${catalogId}/fee
 * @see https://us-east-1.console.aws.amazon.com/ecs/v2/clusters/prod/services/redstripe-prod/tasks?region=us-east-1
 */
export const getProcessingFees = ({ authToken, balanceDue, catalogId, deployment }: GetProcessingFeesParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: `catalog/${catalogId}/fee`,
            authToken,
            deployment,
            path: ApiHosts.Redstripe,
        });
        request.send({ balanceDue });
        request.end((err, response) => {
            return handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            });
        });
    });
